import { useState, useEffect } from 'react'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { mapPropertyToItem } from '@/lib/api/integrations/rentvine.utils'
import { toast } from 'sonner'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { INTEGRATIONS } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import { Heading } from '@/components/ui/typography'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  useFormChangeTracking,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { Button } from '@/components/ui/button'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

const formSchema = z.object({
  accountId: z.string().min(1, 'Account ID is required'),
  clientId: z.string().min(1, 'API Key is required'),
  clientSecret: z.string(),
  defaultPropertyID: z.string().optional(),
  defaultPropertyAddress: z.string().optional(),
  defaultWorkOrderStatusID: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

const SAVED_SECRET_PLACEHOLDER = '••••••••'

export interface RentvineConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
}

export function RentvineConnectForm({
  isEditMode = false,
  onConnected,
}: RentvineConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [isLoading, setIsLoading] = useState(isEditMode)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [propertyItems, setPropertyItems] = useState<
    SearchDropdownItem<string>[]
  >([])
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)
  const [dropdownsFailed, setDropdownsFailed] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountId: '',
      clientId: '',
      clientSecret: '',
      defaultPropertyID: '',
      defaultPropertyAddress: '',
      defaultWorkOrderStatusID: '',
    },
  })

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) {
      setIsLoading(false)
      return
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const rentvine = integrations.find(
          i =>
            i.type === INTEGRATIONS.RENTVINE.TYPE ||
            i.name === INTEGRATIONS.RENTVINE.NAME,
        )
        if (rentvine?.connected) {
          const meta = rentvine.metadata as Record<string, unknown> | undefined
          form.reset({
            accountId: rentvine.accountId ?? '',
            clientId: rentvine.clientId ?? '',
            clientSecret: SAVED_SECRET_PLACEHOLDER,
            defaultPropertyID: String(meta?.defaultPropertyID ?? ''),
            defaultPropertyAddress: String(meta?.defaultPropertyAddress ?? ''),
            defaultWorkOrderStatusID: String(
              meta?.defaultWorkOrderStatusID ?? '',
            ),
          })
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing credentials')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin, form])

  useEffect(() => {
    if (!currentCompany?.id || !isEditMode) return

    let cancelled = false
    setIsLoadingDropdowns(true)
    setDropdownsFailed(false)

    const request = isSuperAdmin
      ? integrationsClient.rentvine.getAdminProperties(currentCompany.id)
      : integrationsClient.rentvine.getProperties()

    request
      .then(properties => {
        if (cancelled) return
        setPropertyItems(properties.map(mapPropertyToItem))
      })
      .catch(error => {
        if (cancelled) return
        console.error('Error fetching properties:', error)
        setDropdownsFailed(true)
        toast.error(
          'Unable to fetch properties from Rentvine. Please try again later.',
        )
      })
      .finally(() => !cancelled && setIsLoadingDropdowns(false))

    return () => {
      cancelled = true
    }
  }, [currentCompany?.id, isSuperAdmin, isEditMode])

  const handleSubmit = async (values: FormValues) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    const accessKey = values.clientId
    const accountId = values.accountId
    const secretUnchanged =
      isEditMode &&
      (values.clientSecret === SAVED_SECRET_PLACEHOLDER ||
        values.clientSecret === '')

    if (!isEditMode && !values.clientSecret?.trim()) {
      toast.error('API Secret is required when connecting')
      return
    }

    const buildMetadata = () => {
      const meta: Record<string, string | number> = {}
      const propId = values.defaultPropertyID?.trim()
      if (propId) meta.defaultPropertyID = propId
      const propAddress = values.defaultPropertyAddress?.trim()
      if (propAddress) meta.defaultPropertyAddress = propAddress
      const statusId = parseInt(values.defaultWorkOrderStatusID ?? '', 10)
      if (!isNaN(statusId) && values.defaultWorkOrderStatusID) {
        meta.defaultWorkOrderStatusID = statusId
      }
      return Object.keys(meta).length > 0 ? meta : undefined
    }
    const metadata = buildMetadata()

    try {
      setIsSubmitting(true)
      if (isEditMode) {
        const options: {
          clientSecret?: string
          metadata?: Record<string, string | number>
        } = {}
        if (!secretUnchanged && values.clientSecret?.trim()) {
          options.clientSecret = values.clientSecret
        }
        if (metadata) options.metadata = metadata

        if (isSuperAdmin) {
          await integrationsClient.rentvine.updateAdminCredentials(
            currentCompany.id,
            accessKey,
            accountId,
            options,
          )
        } else {
          await integrationsClient.rentvine.updateCredentials(
            accessKey,
            accountId,
            options,
          )
        }
        toast.success('Rentvine credentials updated successfully!')
      } else {
        const createMetadata = metadata
        if (isSuperAdmin) {
          await integrationsClient.rentvine.createAdminCredentials(
            currentCompany.id,
            accessKey,
            values.clientSecret!,
            accountId,
            createMetadata,
          )
        } else {
          await integrationsClient.rentvine.createCredentials(
            accessKey,
            values.clientSecret!,
            accountId,
            createMetadata,
          )
        }
        toast.success('Rentvine integration connected successfully!')
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} Rentvine:`,
        error,
      )
      toast.error(
        `Failed to ${isEditMode ? 'update' : 'connect'} Rentvine integration`,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const FloatingIslandContent = () => {
    const { hasUnsavedChanges } = useFormChangeTracking()

    return (
      <FloatingActionIslandContent
        show={hasUnsavedChanges}
        componentId="rentvine-form"
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl">
          <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
            <span>
              {isEditMode
                ? 'Update your credentials and work order settings for Rentvine'
                : 'Connect your credentials for Rentvine'}
            </span>
          </div>
          <div className="flex items-center space-x-2 justify-end pointer-events-auto ml-auto">
            <Button
              onClick={form.handleSubmit(handleSubmit)}
              disabled={isSubmitting}
              size="sm"
              variant="form"
            >
              <span>
                {isSubmitting
                  ? isEditMode
                    ? 'Updating...'
                    : 'Connecting...'
                  : isEditMode
                    ? 'Update'
                    : 'Connect'}
              </span>
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        noValidate
        className="space-y-6 max-w-2xl"
      >
        <FormField
          control={form.control}
          name="accountId"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>Account ID (Subdomain)</FormLabel>
              <FormControl>
                <Input
                  placeholder="mycompany"
                  disabled={isSubmitting}
                  autoFocus
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Your Rentvine subdomain (the part before .rentvine.com)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="clientId"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>API Key</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter your Rentvine API access key"
                  disabled={isSubmitting}
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Your Rentvine API access key found in your User and API Settings
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="clientSecret"
          render={({ field }) => (
            <FormItem className="max-w-md">
              <FormLabel>API Secret</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder="Enter your Rentvine API secret"
                  disabled={isSubmitting}
                  {...field}
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  onFocus={() => {
                    if (field.value === SAVED_SECRET_PLACEHOLDER) {
                      field.onChange('')
                    }
                  }}
                />
              </FormControl>
              <FormDescription>Your Rentvine API secret</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-4 pt-6 border-t">
          <Heading variant="subsection-title">Work Order Settings</Heading>
          <p className="text-sm text-muted-foreground">
            Configure defaults for work orders pushed to Rentvine when property
            matching fails or for status assignment.
          </p>

          {isEditMode && (
            <FormField
              control={form.control}
              name="defaultPropertyID"
              render={({ field }) => {
                const value = field.value ?? ''
                const selected =
                  propertyItems.find(item => item.id === value) ?? null
                return (
                  <FormItem className="max-w-md">
                    <FormLabel>Default Property</FormLabel>
                    <FormControl>
                      {dropdownsFailed ? (
                        <Input
                          placeholder="Enter Rentvine property ID"
                          value={value}
                          onChange={e => field.onChange(e.target.value)}
                          disabled={isSubmitting}
                        />
                      ) : (
                        <SearchDropdown<string>
                          items={propertyItems}
                          selectedItems={selected}
                          onSelect={item => {
                            const sel = Array.isArray(item) ? item[0] : item
                            field.onChange(sel?.data ?? '')
                          }}
                          buttonLabel={
                            isLoadingDropdowns
                              ? 'Loading properties...'
                              : (selected?.label ?? 'Select a property...')
                          }
                          placeholder="Select a property..."
                          searchPlaceholder="Search properties..."
                          emptyMessage="No properties found"
                          buttonClassName="w-full [&_.truncate]:ml-0"
                          disabled={isSubmitting || isLoadingDropdowns}
                        />
                      )}
                    </FormControl>
                    <FormDescription>
                      Used when no property is matched from the conversation.
                      Takes precedence over the address fallback below.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )
              }}
            />
          )}

          <FormField
            control={form.control}
            name="defaultPropertyAddress"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>Default Property Address</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g. 123 Main St, Anytown, CA 12345"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Fallback address lookup when no property is matched and no
                  default property ID is set above.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="defaultWorkOrderStatusID"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>Default Status</FormLabel>
                <Select
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  disabled={isSubmitting}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a status..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="1">1 – Pending</SelectItem>
                    <SelectItem value="2">2 – Open</SelectItem>
                    <SelectItem value="3">3 – Closed</SelectItem>
                    <SelectItem value="4">4 – On Hold</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  Rentvine status for new work orders. Uses 1 (Pending) if not
                  set.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>

      <FloatingIslandContent />
    </Form>
  )
}
